import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Magnetic from '../components/ux/Magnetic';

gsap.registerPlugin(ScrollTrigger);

const {
  FiTarget,
  FiCpu,
  FiShield,
  FiGlobe,
  FiLayers,
  FiZap,
  FiUser
} = FiIcons;

const values = [
  {
    icon: FiTarget,
    title: "Precision",
    desc: "We build things right the first time. Attention to detail is non-negotiable in every line of code and architectural decision."
  },
  {
    icon: FiCpu,
    title: "Intelligence",
    desc: "Every system we build is designed with AI-readiness from day one, ensuring your infrastructure is prepared for the future."
  },
  {
    icon: FiShield,
    title: "Integrity",
    desc: "We are honest about timelines, scope, and what's technically possible. We value long-term partnerships over short-term gains."
  }
];

const technologies = [
  ".NET", "React", "Python", "Microsoft Azure", "TensorFlow", "PyTorch",
  "Web API", "MVC", "REST", "Machine Learning", "Computer Vision",
  "NLP", "Git", "AWS", "Google Cloud"
];

const teamMembers = [
  {
    name: "Technical Leadership",
    role: "Principal Architect & Founder",
    bio: "Over a decade of experience architecting enterprise .NET systems and leading cross-functional delivery teams across manufacturing, finance, and healthcare. Microsoft Azure certified with deep expertise in microservices and AI integration.",
    specialties: [".NET Architecture", "Azure Cloud", "AI Strategy"]
  },
  {
    name: "AI & ML Engineering",
    role: "Senior AI Engineer",
    bio: "Specialist in deep learning, computer vision, and NLP with production deployments processing millions of data points. Background in research-grade model development and real-world AI pipeline design.",
    specialties: ["TensorFlow", "PyTorch", "Computer Vision"]
  },
  {
    name: "Full-Stack Development",
    role: "Senior Software Engineer",
    bio: "Expert in building scalable web applications and RESTful APIs. Strong background in React, .NET Core, and database design for high-throughput enterprise systems.",
    specialties: ["React", ".NET Core", "Web APIs"]
  }
];

const About = () => {
  const containerRef = useRef(null);
  const heroRef    = useRef(null);
  const storyRef   = useRef(null);
  const quoteRef   = useRef(null);
  const valuesRef  = useRef([]);
  const teamRef    = useRef([]);
  const techRef    = useRef([]);
  const parallaxRef = useRef(null);
  
  const push = (b) => (el) => { if (el) b.current.push(el); };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Parallax abstract element
      gsap.to(parallaxRef.current, {
        y: '20%',
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });

      // Hero — slides in from left on load
      gsap.fromTo(heroRef.current,
        { autoAlpha: 0, x: -32 },
        { autoAlpha: 1, x: 0, duration: 0.9, ease: 'expo.out' }
      );
      // Origin story panel
      gsap.fromTo(storyRef.current,
        { autoAlpha: 0, y: 40 },
        { autoAlpha: 1, y: 0, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: storyRef.current, start: 'top 82%', once: true } }
      );
      // Quote callout
      gsap.fromTo(quoteRef.current,
        { autoAlpha: 0, scale: 0.88 },
        { autoAlpha: 1, scale: 1, duration: 1.1, ease: 'expo.out',
          scrollTrigger: { trigger: quoteRef.current, start: 'top 80%', once: true } }
      );
      // Values cards
      const vCards = valuesRef.current.filter(Boolean);
      gsap.fromTo(vCards,
        { autoAlpha: 0, y: 32 },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out',
          stagger: { amount: 0.35, from: 'start' },
          scrollTrigger: { trigger: vCards[0], start: 'top 84%', once: true } }
      );
      // Team cards
      const tCards = teamRef.current.filter(Boolean);
      gsap.fromTo(tCards,
        { autoAlpha: 0, y: 32 },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out',
          stagger: { amount: 0.35, from: 'start' },
          scrollTrigger: { trigger: tCards[0], start: 'top 84%', once: true } }
      );
      // Tech tags
      const tags = techRef.current.filter(Boolean);
      gsap.fromTo(tags,
        { autoAlpha: 0, scale: 0.85, y: 8 },
        { autoAlpha: 1, scale: 1, y: 0, duration: 0.4, ease: 'back.out(1.7)',
          stagger: { amount: 0.4, from: 'center' },
          scrollTrigger: { trigger: tags[0], start: 'top 88%', once: true } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="pt-32 pb-0 relative overflow-hidden bg-white dark:bg-slate-950 transition-colors duration-300">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      {/* Section 1: Who We Are */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-16 md:mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
          <div ref={heroRef} style={{ opacity: 0 }} className="text-center lg:text-left">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6 md:mb-8 leading-tight">
              Engineering <span className="text-gradient-cyan">Intelligent</span> Systems
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mb-6">
              Aetrix Logic is a technology company built around a simple conviction: that intelligent software should be accessible to any business, not just the enterprise giants.
            </p>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
              We bring together expertise in software engineering, artificial intelligence, and cloud infrastructure to deliver systems that are built to last and designed to grow.
            </p>
          </div>

          <div ref={parallaxRef} className="relative mt-8 lg:mt-0">
            <div className="absolute inset-0 bg-cyan-100/40 dark:bg-cyan-900/20 blur-[100px] rounded-full animate-pulse"></div>
            <div className="relative aspect-square bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 flex items-center justify-center overflow-hidden shadow-sm">
              {/* Abstract Geometry */}
              <div className="absolute inset-0 opacity-20 dark:opacity-10">
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-cyan-300 transform rotate-45 scale-150"></div>
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-blue-300 transform -rotate-12 scale-125"></div>
              </div>
              <SafeIcon icon={FiLayers} className="text-6xl md:text-8xl text-cyan-500 relative z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Our Origin Story */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900 border-y border-slate-100 dark:border-slate-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={storyRef} style={{ opacity: 0 }} className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <div className="md:w-1/3">
              <div className="flex items-center gap-4 mb-4">
                <SafeIcon icon={FiGlobe} className="text-2xl md:text-3xl text-cyan-600 dark:text-cyan-400" />
                <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white uppercase tracking-wider">Our Story</h2>
              </div>
              <div className="h-1 w-20 bg-cyan-500"></div>
            </div>
            <div className="md:w-2/3">
              <p className="text-slate-700 dark:text-slate-300 text-lg md:text-xl leading-relaxed mb-6 md:mb-8">
                Aetrix Logic was founded with a clear observation: too many businesses were trapped between expensive enterprise vendors who over-promise and under-deliver, and freelancers who lack the depth for mission-critical systems.
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mb-6">
                We chose the .NET and Microsoft Azure stack deliberately — it's the backbone of enterprise software worldwide, and mastering it deeply allows us to build systems that integrate with real corporate infrastructure, not just prototypes. When the AI revolution accelerated, we didn't pivot from a different discipline. We extended our engineering foundation to include TensorFlow, PyTorch, and production-grade ML pipelines — because intelligent software needs a rock-solid platform underneath.
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mb-6">
                Our team has delivered across manufacturing floors in India, financial services firms in the UK, and logistics operations spanning both geographies. Every engagement has reinforced the same lesson: technology only creates value when it's shaped around how a business actually works — not how a vendor thinks it should.
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                Today, we partner with clients over months and years, not just sprints. We start with deep discovery, build with precision, and stay involved through support and iteration. That's how we turn complex technical challenges into durable competitive advantages.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Mission Callout */}
      <section className="py-20 md:py-32 relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Magnetic strength={0.2}>
            <div ref={quoteRef} style={{ opacity: 0 }} className="relative inline-block cursor-default" data-cursor="hover">
              <div className="absolute -top-6 md:-top-12 -left-4 md:-left-12 text-7xl md:text-9xl text-cyan-200 dark:text-cyan-900/40 font-serif leading-none">"</div>
              <h2 className="text-2xl md:text-6xl font-black text-slate-900 dark:text-white italic relative z-10 px-4 leading-tight md:leading-normal">
                We don't just write code — <br className="hidden md:block"/>
                <span className="text-gradient-cyan">we engineer outcomes.</span>
              </h2>
              <div className="absolute -bottom-6 md:-bottom-12 -right-4 md:-right-12 text-7xl md:text-9xl text-cyan-200 dark:text-cyan-900/40 font-serif leading-none rotate-180">"</div>
            </div>
          </Magnetic>
        </div>
      </section>

      {/* Section 4: Our Values */}
      <section className="py-16 md:py-24 relative z-10 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                ref={push(valuesRef)}
                style={{ opacity: 0 }}
                className="p-8 md:p-10 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-cyan-300 dark:hover:border-cyan-500 transition-all group shadow-sm hover:shadow-xl hover:-translate-y-2 cursor-default"
                data-cursor="hover"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={value.icon} className="text-xl md:text-2xl text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white mb-3 md:mb-4">{value.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Our Team */}
      <section className="py-16 md:py-24 relative z-10 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              A Small, <span className="text-gradient-cyan">Senior Team</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-base md:text-lg">
              We keep our team deliberately small so every engagement gets senior-level attention. No juniors, no hand-offs — the people you meet are the people who build your system.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                ref={push(teamRef)}
                style={{ opacity: 0 }}
                className="p-6 md:p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-cyan-300 dark:hover:border-cyan-500 transition-all group shadow-sm hover:shadow-xl hover:-translate-y-2 cursor-default"
                data-cursor="hover"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex items-center justify-center mb-6 group-hover:border-cyan-300 dark:group-hover:border-cyan-500 transition-colors">
                  <SafeIcon icon={FiUser} className="text-xl md:text-2xl text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="text-base md:text-lg font-bold text-slate-900 dark:text-white mb-1">{member.name}</h3>
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400 mb-4">{member.role}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-6">{member.bio}</p>
                <div className="flex flex-wrap gap-2">
                  {member.specialties.map((spec, sIndex) => (
                    <span key={sIndex} className="px-2 py-1 md:px-3 md:py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Technologies */}
      <section className="py-16 md:py-24 relative z-10 border-t border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-2 md:mb-4">Technologies We Work With</h2>
            <p className="text-slate-500 dark:text-slate-400 uppercase tracking-widest text-[10px] md:text-xs font-bold">A modern, enterprise-ready stack</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {technologies.map((tech, index) => (
              <Magnetic key={index} strength={0.3}>
                <span
                  ref={push(techRef)}
                  style={{ opacity: 0 }}
                  className="inline-block px-4 py-2 md:px-6 md:py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-[10px] md:text-xs font-bold uppercase tracking-widest hover:border-cyan-300 dark:hover:border-cyan-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all cursor-default shadow-sm hover:shadow-md"
                  data-cursor="hover"
                >
                  {tech}
                </span>
              </Magnetic>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Padding for Footer */}
      <div className="h-16 md:h-24"></div>
    </div>
  );
};

export default About;